<script setup lang="ts">
import { computed, ref } from 'vue'
import { micromark } from 'micromark'
import { releaseDetail } from '../releaseDetail'
import itemDefinitions from '../generated/itemDefinitions.json'
import ItemIcon from './ItemIcon.vue'
import { Expansions } from '../enums/expansions'
import { FinalShapeDungeonHashes, LightfallDungeonHashes, WitchQueenDungeonHashes } from '../enums/dungeons'
import { BeyondLightSeasonHashes, EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes, RenegadesSeasonHashes, ShadowkeepSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'
import { Release, ReleaseId } from '../enums/releases'
import GenericExpansionIcon from './GenericExpansionIcon.vue'

var props = defineProps<{ expansion: Release, open: boolean }>()
const emit = defineEmits(['toggle'])

const collapsible = ref<HTMLInputElement | null>(null)

const text = computed(() => releaseDetail.get(props.expansion.id)?.longDescription)
const isBase = computed(() => ([
  Expansions.Destiny2
] as ReleaseId[]).includes(props.expansion.id))
const isMajorExpansion = computed(() => ([
  Expansions.CurseOfOsiris, Expansions.Warmind, Expansions.Forsaken, Expansions.AnnualPass,
  Expansions.Shadowkeep, Expansions.BeyondLight, Expansions.Anniversary30th,
  Expansions.TheWitchQueen, Expansions.Lightfall, Expansions.FinalShape,
  Expansions.EdgeOfFate, Expansions.Renegades] as ReleaseId[]).includes(props.expansion.id))
const isDungeonKey = computed(() => ([
  WitchQueenDungeonHashes.Duality, WitchQueenDungeonHashes.SpireOfTheWatcher,
  LightfallDungeonHashes.GhostsOfTheDeep, LightfallDungeonHashes.WarlordsRuin,
  FinalShapeDungeonHashes.VespersHost, FinalShapeDungeonHashes.SunderedDoctrine] as ReleaseId[]).includes(props.expansion.id))
const isSeason = computed(() => ([
  ShadowkeepSeasonHashes.Undying, ShadowkeepSeasonHashes.Dawn, ShadowkeepSeasonHashes.Worthy, ShadowkeepSeasonHashes.Arrivals,
  BeyondLightSeasonHashes.Hunt, BeyondLightSeasonHashes.Chosen, BeyondLightSeasonHashes.Splicer, BeyondLightSeasonHashes.Lost,
  WitchQueenSeasonHashes.Risen, WitchQueenSeasonHashes.Haunted, WitchQueenSeasonHashes.Plunder, WitchQueenSeasonHashes.Seraph,
  LightfallSeasonHashes.Defiance, LightfallSeasonHashes.Deep, LightfallSeasonHashes.Witch, LightfallSeasonHashes.Wish] as ReleaseId[]).includes(props.expansion.id))
const isEpisode = computed(() => ([
  FinalShapeEpisodeHashes.Echoes, FinalShapeEpisodeHashes.Revenant,
  FinalShapeEpisodeHashes.Heresy] as ReleaseId[]).includes(props.expansion.id))
const isRewardsPass = computed(() => ([
  EdgeOfFateSeasonHashes.Reclamation, EdgeOfFateSeasonHashes.AshAndIron,
  RenegadesSeasonHashes.Lawless, RenegadesSeasonHashes.MonumentOfTriumph] as ReleaseId[]).includes(props.expansion.id))

function toggle() {
  if (props.expansion.active) {
    emit('toggle')
  }
}
</script>

<template>
  <div
    class="flex flex-col w-full font-semibold shadow-lg shadow-black/50 rounded-2xl relative overflow-hidden bg-white"
    :class="{ 'opacity-50': !expansion.active }"
  >
    <div
      class="flex items-center gap-2 px-2 py-3 border-black/25 transition-all ease-in-out duration-300 border-b-2 -mb-1"
      :class="{ 'cursor-pointer': expansion.active }"
      @click="toggle"
    >
      <GenericExpansionIcon :expansion="expansion.id" other-classes="h-8 w-8 mx-2" />
      <div>
        <h2 class="text-xl leading-5" :class="{ 'pr-32': isDungeonKey, 'pr-28': isMajorExpansion, 'pr-24': isSeason || isEpisode }">{{ expansion.name }}</h2>
        <span class="text-xs">Released {{ expansion.date.toLocaleDateString('en-US', { dateStyle: 'long' }) }} // {{ expansion.bought }}</span>
        <small v-if="isBase" class="absolute top-4 right-10 text-xs">BASE GAME</small>
        <small v-else-if="isMajorExpansion" class="absolute top-4 right-10 text-xs">EXPANSION</small>
        <small v-else-if="isDungeonKey" class="absolute top-4 right-10 text-xs">DUNGEON KEY</small>
        <small v-else-if="isSeason" class="absolute top-4 right-10 text-xs">SEASON</small>
        <small v-else-if="isEpisode" class="absolute top-4 right-10 text-xs">EPISODE</small>
        <small v-else-if="isRewardsPass" class="absolute top-4 right-10 text-xs">REWARDS PASS</small>
        <img src="../assets/caret.svg" class="absolute top-4 right-4 h-4 w-4 transition-transform duration-350" alt="" :class="{ '-rotate-180': open, 'opacity-75': expansion.active, 'opacity-0': !expansion.active }" />
      </div>
    </div>
    <div
      v-if="expansion.active"
      class="markdown text-sm transition-all ease-in-out duration-300"
      ref="collapsible"
      :style="{ 'max-height': open ? '999px' : '0px' }"
    >
      <div class="px-2 py-3">
        <template v-for="(innerText, i) in text">
          <span v-if="(typeof innerText === 'string')" :key="i" v-html="micromark(innerText)" />
          <div v-if="(typeof innerText !== 'string')" :key="i" class="flex flex-wrap gap-2 mt-2">
            <ItemIcon v-for="(item, j) in innerText" :key="j" :item="(itemDefinitions.find(x => x.id === item) as Item)" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>